// Line-level Myers diff (via the `diff` library) reduced to an index-level op
// stream. Equality is decided on the *normalized* key so ignore-rules work,
// while ops carry the original left/right indices so callers can pull the
// original text and precise line numbers.
import { diffArrays } from 'diff';
import { makeNormalizer } from './normalize.js';

export function splitLines(s) {
  if (!s.length) return [];
  const lines = s.split(/\r?\n/);
  // A single terminating newline shouldn't render as a phantom empty last line
  // (so "a\nb" and "a\nb\n" compare equal). A genuine trailing blank line —
  // "a\nb\n\n" — keeps one empty entry.
  if (lines.length > 1 && lines[lines.length - 1] === '' && /\r?\n$/.test(s)) lines.pop();
  return lines;
}

// The naive line-based literal diff, shaped for the Raw view. Shared by every
// structural engine (json/csv) so the Raw fallback is identical everywhere.
export function rawLineRows(left, right, options = {}) {
  const L = splitLines(left);
  const R = splitLines(right);
  return lineDiff(L, R, options).map((op) =>
    op.op === 'equal'
      ? { cls: '', ln: op.r + 1, text: R[op.r] }
      : op.op === 'del'
        ? { cls: 'del', ln: op.l + 1, text: L[op.l] }
        : { cls: 'add', ln: op.r + 1, text: R[op.r] },
  );
}

/** @returns {Array<{op:'equal'|'del'|'ins', l:number, r:number}>} */
export function lineDiff(L, R, options = {}) {
  const norm = makeNormalizer(options);
  const parts = diffArrays(L.map(norm), R.map(norm));
  const ops = [];
  let l = 0;
  let r = 0;
  for (const part of parts) {
    const n = part.value.length;
    if (part.added) {
      for (let k = 0; k < n; k++) ops.push({ op: 'ins', l: -1, r: r++ });
    } else if (part.removed) {
      for (let k = 0; k < n; k++) ops.push({ op: 'del', l: l++, r: -1 });
    } else {
      for (let k = 0; k < n; k++) ops.push({ op: 'equal', l: l++, r: r++ });
    }
  }
  return ops;
}
